// TodayPage — the daily briefing (BUILD-BRIEF §6 — interactions agent).
//
// Rail: Unheld · Signals · Ball · Stale · Cooling, from GET /api/today — a value the server
// returns as `null` renders `fv--unknown` "not collected", never 0 (TodayResponse's own
// doc comment). Zones: meetings today (honest empty until the calendar exists), the ball line,
// overnight (the runs of the last day, each a door to its desk), cooling, signals (empty, M5).
import { useCallback, useEffect, useState } from 'react';
import EmptyState from '../components/EmptyState.js';
import { LogTouchpointModal } from '../components/Timeline.js';
import { Att, Fig, Lnk, Mast, PageShell, Rail, RailCell, Ref, Sheet, Tag, Tbl, Zone, ZoneHead } from '../components/PageKit.js';
import { useToast } from '../components/Toast.js';
import { useLocale, useT } from '../i18n/index.js';
import { href, open, ROUTES } from '../routes.js';
import { getToday, type TodayView } from '../api/today.js';
import { listRuns } from '../api/runs.js';
import type { Run } from '../../shared/types.js';
import { completeAction, dismissAction } from '../api/scoring.js';
import { ApiError } from '../api/client.js';
import type { CommitmentView } from '../../shared/types.js';
import './TodayPage.de.js';
import './TodayPage.fr.js';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The UNHELD cell's detail line, one phrase per proposal kind. Both forms are catalogue keys,
 * because a count is not a suffix: '11 affiliation' is not English, and neither the German nor
 * the French plural can be reached by gluing an 's' onto a singular noun. A kind the queue
 * grows later and this table does not know still reads as a count and a word, never a wrong
 * plural — but the ten kinds `proposals.kind` allows are all here.
 */
const UNHELD_KIND: Record<string, readonly [one: string, other: string]> = {
  fact: ['{n} fact', '{n} facts'],
  affiliation: ['{n} affiliation', '{n} affiliations'],
  touchpoint: ['{n} touchpoint', '{n} touchpoints'],
  commitment: ['{n} commitment', '{n} commitments'],
  persona: ['{n} persona', '{n} personas'],
  draft: ['{n} draft', '{n} drafts'],
  candidate: ['{n} candidate', '{n} candidates'],
  signal_action: ['{n} signal action', '{n} signal actions'],
  organisation: ['{n} organisation', '{n} organisations'],
  person: ['{n} person', '{n} people'],
};

/**
 * THE BALL CELL'S DETAIL, COMPOSED HERE. The route also answers a pre-composed `ball_detail`,
 * but it is an English sentence carrying the raw ISO `due_on` — two things this console does
 * not do: a date reads `dd.mm.yyyy` (ATTEST), and every sentence passes through `t()`, or a
 * German console reads English in one rail cell and German in the other four. The same fact is
 * already in `ball_line` — every open commitment with its counterpart and its due date — so the
 * cell writes the sentence itself, from the EARLIEST overdue row, naming the counterpart
 * organisation-first exactly as the route's own query does. `null` when nothing is overdue.
 */
export function ballDetailText(
  ballLine: CommitmentView[],
  todayIso: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
  fmtDate: (iso: string | null | undefined) => string,
): string | null {
  const oldest = ballLine.reduce<CommitmentView | null>(
    (worst, c) => (c.due_on && c.due_on < todayIso && (!worst || c.due_on < worst.due_on!) ? c : worst),
    null,
  );
  if (!oldest) return null;
  return t('{who} overdue since {date}', {
    who: oldest.organisation_name || oldest.person_name || oldest.ref,
    date: fmtDate(oldest.due_on),
  });
}

export default function TodayPage() {
  const t = useT();
  const { fmtDate, fmtDateTime, tp } = useLocale();
  const [data, setData] = useState<TodayView | null>(null);
  // OVERNIGHT is the runs of the last day. `null` while nobody has answered — the zone then says
  // so rather than printing a zero it did not count.
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [acting, setActing] = useState<number | null>(null);
  const toast = useToast();

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getToday()
      .then((r) => setData(r))
      .catch((err) => setError(err instanceof ApiError ? err.message : t('Server error')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // A failed read leaves `runs` null and the zone silent, exactly like a nav count: a route that
  // is absent must never turn into "0 runs" (src/client/CLAUDE.md, the poll that tolerates
  // failure).
  useEffect(() => {
    let alive = true;
    listRuns({ limit: 12 })
      .then((r) => { if (alive) setRuns(r.items); })
      .catch(() => { if (alive) setRuns(null); });
    return () => { alive = false; };
  }, []);

  /**
   * Close one action. `done` records that the operator did it; `dismiss` records that they
   * refused it — and the scoring service respects a dismissal for seven days rather than
   * proposing the same move again on the next five-minute pass.
   */
  const closeAction = useCallback(async (id: number, verb: 'done' | 'dismiss') => {
    setActing(id);
    try {
      if (verb === 'done') await completeAction(id);
      else await dismissAction(id);
      toast('ok', verb === 'done'
        ? t('Marked done.')
        : t('Dismissed. It will not be proposed again for seven days.'));
      load();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Server error'));
    } finally {
      setActing(null);
    }
  }, [load, t, toast]);

  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);
  const weekday = t(WEEKDAYS[now.getDay()]);

  const unheldDetail = data?.unheld_by_kind && Object.keys(data.unheld_by_kind).length
    ? Object.entries(data.unheld_by_kind)
        .map(([k, n]) => {
          const forms = UNHELD_KIND[k];
          return forms ? tp(n, forms[0], forms[1]) : t('{n} {kind}', { n, kind: t(k) });
        })
        .join(' · ')
    : (data?.unheld ? t('unheld proposals') : t('nothing waiting'));

  // The last day's runs. A day, not the literal 22:00–05:30 window: a run the operator started
  // this morning is part of what the console has to account for, and hiding it behind a clock
  // would make the zone lie by omission.
  const dayRuns = runs
    ? runs.filter((r) => Date.now() - Date.parse(r.started_at ?? r.created_at) < 86_400_000)
    : null;

  const ballDetail = ballDetailText(
    data?.ball_line ?? [], todayIso, t, fmtDate,
  ) ?? (data?.ball ? t('{n} them', { n: data.ball.them }) : t('not collected'));

  return (
    <PageShell>
      <Mast
        kicker={weekday}
        title={t('Today')}
        sub={<><span className="voice-figure">{fmtDate(todayIso)}</span></>}
        verbs={(
          <>
            <button className="btn" onClick={() => setLogOpen(true)}>{t('Log a touchpoint')}</button>
            <button className="btn btn-accent" onClick={() => open(ROUTES.review())}>
              {t('Review {n} →', { n: data?.unheld ?? 0 })}
            </button>
          </>
        )}
      />

      {error && (
        <EmptyState kind="error" title={t('Could not load')} detail={error} onRetry={load} />
      )}

      {!error && (
        <Rail>
          <RailCell
            label={t('Unheld')} go="→" onClick={() => open(ROUTES.review())}
            value={data?.unheld ?? (loading ? '…' : 0)}
            tone={data?.unheld ? 'attend' : 'ink'}
            detail={unheldDetail}
          />
          {/* `null` is "no monitor has run and nothing was ever raised" — not collected. A
              monitor that exists and found nothing is a COUNTED zero, and reads `fv--nil`. */}
          <RailCell
            label={t('Signals')} go="→" onClick={() => open(ROUTES.signals())}
            value={data?.signals ?? (loading ? '…' : t('not collected'))}
            tone={data?.signals == null ? 'unknown' : data.signals ? 'attend' : 'nil'}
            detail={data?.signals == null
              ? t('no monitor exists and nothing has been raised')
              : (data.signals_detail ?? t('new signals'))}
          />
          <RailCell
            label={t('Ball')}
            value={data?.ball?.us ?? '—'}
            unit={data?.ball ? t('with us') : undefined}
            tone={data?.ball && data.ball.late > 0 ? 'broken' : 'ink'}
            detail={ballDetail}
          />
          <RailCell
            label={t('Stale')} go="→" onClick={() => open(ROUTES.pipeline())}
            value={data?.stale ?? (loading ? '…' : 0)}
            unit={t('deals')}
            tone={data?.stale ? 'attend' : 'ink'}
            detail={data?.stale_detail ?? t('no deal is stale')}
          />
          <RailCell
            label={t('Cooling')} go="→" onClick={() => open(ROUTES.people())}
            value={data?.cooling ?? (loading ? '…' : 0)}
            unit={t('people')}
            tone={data?.cooling ? 'attend' : 'ink'}
            detail={data?.cooling_detail ?? t('everyone is within cadence')}
          />
        </Rail>
      )}

      {/* ── the day's headline: one action per person, highest score first (§10) ── */}
      <Zone id="next-best-actions">
        <ZoneHead
          label={t('Next best actions')}
          count={data?.actions ? <Fig>{data.actions.length}</Fig> : undefined}
          note={t('one per person · chosen by the highest-scoring reason')}
          route={<button type="button" className="zone-route" onClick={() => open(ROUTES.people())}>{t('People')} →</button>}
        />
        {data?.actions == null ? (
          <Sheet pad>
            <p className="zone-empty">
              {t('Nothing has been scored yet. The rules run at 05:00 and on every interaction, signal and commitment; each action names the commitment, the signal or the cadence that produced it, with its ref and its date.')}
            </p>
          </Sheet>
        ) : data.actions.length === 0 ? (
          <Sheet pad>
            <p className="zone-empty">
              {t('Everyone was scored and nobody needs a move: no open commitment with us, no signal in thirty days, and every cadence is being kept.')}
            </p>
          </Sheet>
        ) : (
          <Sheet clip>
            {data.actions.map((a) => {
              const byAgent = !!a.why_now_by && a.why_now_by !== 'rules';
              return (
                <div className="row" style={{ gridTemplateColumns: '1fr 96px 150px' }} key={a.id}>
                  <span>
                    <button type="button" className="lnk" onClick={() => open(ROUTES.person({ id: a.person_id, anchor: 'why-now' }))}>
                      <span className="w">{a.person_name ?? t('not named')}</span>
                    </button>
                    {' '}<Tag tone={a.kind === 'wait' ? 'neutral' : 'attend'}>{t(a.kind)}</Tag>
                    {a.person_ref && <> <span className="src-ref">{a.person_ref}</span></>}
                    <br />
                    <span className="voice-record">
                      {byAgent && <><Att kind="proposed" title={t('written by {by}', { by: a.why_now_by ?? '' })} /> </>}
                      {a.why_now ?? t('no reason was recorded')}
                    </span>
                  </span>
                  <span className="voice-figure" title={t('score')}>{a.score ?? '—'}</span>
                  <span className="d" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" className="lnk" disabled={acting === a.id} onClick={() => { void closeAction(a.id, 'done'); }}>
                      {t('Done')}
                    </button>
                    <button type="button" className="lnk" disabled={acting === a.id} onClick={() => { void closeAction(a.id, 'dismiss'); }}>
                      {t('Dismiss')}
                    </button>
                  </span>
                </div>
              );
            })}
          </Sheet>
        )}
      </Zone>

      <Zone>
        <ZoneHead label={t('Meetings today')} count={data?.meetings_today.length ?? 0} />
        {!data?.meetings_today.length ? (
          <Sheet pad>
            <p className="zone-empty">{t('No calendar feed is connected yet. This zone lists any interaction of kind meeting dated today; today has none on record.')}</p>
          </Sheet>
        ) : (
          data.meetings_today.map((m) => (
            <Sheet pad key={m.interaction_id} style={{ marginBottom: 12 }}>
              <div className="move-head">
                <h2 style={{ fontSize: 'var(--t-h3)' }}>
                  {m.title} <span className="src-ref" style={{ fontSize: '.8em', fontWeight: 500, color: 'var(--ink-3)' }}>{m.ref}</span>
                </h2>
              </div>
              <p className="read" style={{ marginTop: 6 }}>
                {m.occurred_at && <span className="voice-figure">{m.occurred_at.slice(0, 5)}</span>}
                {m.occurred_at && m.organisation_name && <span className="sep">·</span>}
                {m.organisation_name}
                {(m.occurred_at || m.organisation_name) && m.people.length > 0 && <span className="sep">·</span>}
                {m.people.length > 0 && <>{t('with')} <b>{m.people.join(', ')}</b></>}
              </p>
            </Sheet>
          ))
        )}
      </Zone>

      <Zone>
        <ZoneHead
          label={t('Ball line')}
          count={t('{n} open', { n: data?.ball_line.length ?? 0 })}
          note={t('who owes whom · discharge needs evidence')}
        />
        {!data?.ball_line.length ? (
          <Sheet pad>
            <p className="zone-empty">{t('No open commitments. A commitment is created from an interaction, or logged directly, and stays open until it is discharged with evidence or dropped with a reason.')}</p>
          </Sheet>
        ) : (
          <Sheet clip>
            <Tbl>
              <thead>
                <tr><th>{t('Owner')}</th><th>{t('Commitment')}</th><th>{t('Counterpart')}</th><th className="num">{t('Due')}</th><th>{t('Source')}</th></tr>
              </thead>
              <tbody>
                {data.ball_line.map((c) => {
                  const isLate = c.owner === 'us' && !!c.due_on && c.due_on < todayIso;
                  return (
                    <tr key={c.id}>
                      <td><Tag tone={isLate ? 'broken' : c.owner === 'us' ? 'attend' : 'neutral'}>{t(c.owner)}{isLate ? ` · ${t('late')}` : ''}</Tag></td>
                      <td>{c.text}</td>
                      <td>{[c.person_name, c.organisation_name].filter(Boolean).join(' · ') || t('not named')}</td>
                      <td className="num voice-figure" style={isLate ? { color: 'var(--broken)' } : undefined}>{c.due_on ? fmtDate(c.due_on) : t('not set')}</td>
                      <td>{c.source_ref ? <span className="src-ref">{c.source_ref}</span> : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Tbl>
          </Sheet>
        )}
      </Zone>

      {/* ── overnight · the runs, each a door to its own desk (R3f §2) ───── */}
      <Zone>
        <ZoneHead
          label={t('Overnight')}
          /* A count of runs is arithmetic — no claim mark. */
          count={dayRuns ? <Fig>{dayRuns.length}</Fig> : undefined}
          note={t('22:00 – 05:30')}
          route={<button className="zone-route" onClick={() => open(ROUTES.agents())}>{t('Agents →')}</button>}
        />
        {!dayRuns || dayRuns.length === 0 ? (
          <Sheet pad>
            <p className="zone-empty">
              {runs === null
                ? t('The runtime has not reported. Nothing is claimed about the night either way.')
                : t('No run in the last day. The inside agents triage mail, draft replies and read pages overnight; each one appears here with its ref, and the ref opens the desk that shows what it did.')}
            </p>
          </Sheet>
        ) : (
          <Sheet clip>
            {dayRuns.map((r) => (
              <div className="row" style={{ gridTemplateColumns: '110px 1fr auto 110px' }} key={r.id}>
                <span>
                  <Lnk href={href(ROUTES.run({ id: r.id }))} title={t('Open this run’s desk')}><Ref>{r.ref}</Ref></Lnk>
                </span>
                <span>
                  {r.skill}
                  {r.subject_label && <span className="d"> · {r.subject_label}</span>}
                </span>
                <span className="d">
                  {t('filed')} <Fig className={r.proposals_filed === 0 ? 'fv--nil' : undefined}>{r.proposals_filed}</Fig>
                </span>
                <span className="d voice-figure" style={{ textAlign: 'right' }}>{fmtDateTime(r.started_at ?? r.created_at)}</span>
              </div>
            ))}
          </Sheet>
        )}
      </Zone>

      <Zone>
        <ZoneHead label={t('Cooling')} note={t('tier cadence')} route={<button className="zone-route" onClick={() => open(ROUTES.people())}>{t('People →')}</button>} />
        {!data?.cooling_people.length ? (
          <Sheet pad>
            <p className="zone-empty">{t('Nobody is past their cadence. A person cools when more days pass since the last logged interaction than their tier allows.')}</p>
          </Sheet>
        ) : (
          <Sheet clip>
            {data.cooling_people.map((p) => (
              <div className="row" style={{ gridTemplateColumns: '70px 1fr auto' }} key={p.person_id}>
                <span className="k">{p.tier} · {p.cadence_days}d</span>
                <span>{p.name}{p.organisation_name ? ` · ${p.organisation_name}` : ''}</span>
                <span className="voice-figure" style={{ color: 'var(--attend)' }}>{p.days_since}d</span>
              </div>
            ))}
          </Sheet>
        )}
      </Zone>

      {/* ── the five newest observations nobody has dispositioned (§8, §10) ── */}
      <Zone id="signals">
        <ZoneHead
          label={t('Signals')}
          count={data?.signals_line?.length ? <Fig>{data.signals_line.length}</Fig> : undefined}
          note={t('every signal cites what it was read from')}
          route={<button type="button" className="zone-route" onClick={() => open(ROUTES.signals())}>{t('Signals')} →</button>}
        />
        {data?.signals == null ? (
          <Sheet pad>
            <p className="zone-empty">{t('No monitor exists and nothing has been raised. A monitor is a target with a cadence; its runs raise signals here, each citing a snapshot or a publication verbatim, with a suggested action waiting to be held.')}</p>
          </Sheet>
        ) : !data.signals_line?.length ? (
          <Sheet pad>
            <p className="zone-empty">{t('The monitors ran and raised nothing new. That is a counted zero, not an absence of watching.')}</p>
          </Sheet>
        ) : (
          <Sheet clip>
            {data.signals_line.map((s) => (
              <div className="row" style={{ gridTemplateColumns: '86px 1fr 110px' }} key={s.id}>
                <span className="k">{t(s.kind)}</span>
                <span>
                  <span className="w">{s.headline}</span>
                  {s.subject_label && <span className="d"> · {s.subject_label}</span>}
                  {' '}<span className="src-ref">{s.ref}</span>
                  {s.quote && (
                    <>
                      <br />
                      <span className="voice-record">“{s.quote}”</span>
                    </>
                  )}
                </span>
                <span className="d" style={{ textAlign: 'right' }}>
                  <Tag tone={s.severity === 'high' ? 'broken' : s.severity === 'attend' ? 'attend' : 'neutral'}>{t(s.severity)}</Tag>
                  <br />
                  <span className="voice-figure">{fmtDate(s.first_seen.slice(0, 10))}</span>
                </span>
              </div>
            ))}
          </Sheet>
        )}
      </Zone>

      <LogTouchpointModal open={logOpen} onClose={() => setLogOpen(false)} onLogged={load} />
    </PageShell>
  );
}
